import { useEffect, useState } from 'react'
import api from '../services/api'

// Layout de importação dos arquivos dos postos (GeneXus TLAYPOS → frota.laypos/lapost), em Combustível → Parâmetros.
interface Linha { layposini: string; laypostam: string; laytipcam: string; laydcmcam: string; layatrnom: string; laytiplei: string; layvalcons: string }
interface Layout { layposcod: string; layposdes: string; laypossta: string; linhas: Linha[] }
interface ItemLista { layposcod: number; layposdes: string; laypossta: string; linhas: number }

const TIPOS = [['A', 'Alfanumérico'], ['N', 'Numérico'], ['D', 'Data']]
const DECIMAIS = [['0', 'Zero'], ['1', 'Um'], ['2', 'Dois'], ['3', 'Três'], ['4', 'Quatro']]
const LEITURAS = [['A', 'Arquivo'], ['L', 'Layout']]
const ATRIBUTOS = [
  ['C', 'Cabeçalho'], ['P', 'Primeira Linha'], ['CLICOD', 'CNPJ do Posto'], ['NTVNUM', 'Cupom Fiscal'],
  ['DIA', 'Dia Abastecimento'], ['MES', 'Mês Abastecimento'], ['ANO', 'Ano Abastecimento'],
  ['HORA', 'Hora Abastecimento'], ['MINUTO', 'Minuto Abastecimento'],
  ['VEICOD', 'Código do Veículo'], ['VEIPLA', 'Placa do Veículo'], ['VEIODO', 'Hodômetro'],
  ['QTDE', 'Quantidade de Litros'], ['VALORT', 'Valor Total'], ['COMBUS', 'Combustível'],
]

const linhaVazia = (ini: number): Linha => ({ layposini: String(ini), laypostam: '', laytipcam: 'A', laydcmcam: '0', layatrnom: '', laytiplei: 'A', layvalcons: '' })
const NOVO: Layout = { layposcod: '', layposdes: '', laypossta: 'A', linhas: [linhaVazia(1)] }

function Campo({ label, width, children }: { label: string; width?: number | string; children: React.ReactNode }) {
  return (
    <div className="field-cad" style={{ width }}>
      <label style={{ display: 'block', minHeight: '1.1rem', lineHeight: 1.2 }}>{label}</label>
      {children}
    </div>
  )
}

const secao: React.CSSProperties = {
  background: '#f7faff', border: '1px solid #eef0f4', borderLeft: '4px solid #1a6ea8', borderRadius: '0.5rem', padding: '0.55rem 0.85rem',
  marginBottom: '0.55rem', display: 'flex', flexWrap: 'wrap', gap: '0.5rem 0.9rem', alignItems: 'flex-end',
}
const th: React.CSSProperties = { background: '#1b3f7b', color: '#fff', fontSize: '0.75rem', fontWeight: 600, padding: '6px 6px', textAlign: 'left', lineHeight: 1.15, verticalAlign: 'middle', overflow: 'hidden' }
const td: React.CSSProperties = { padding: '3px 4px', borderBottom: '1px solid #eef0f4' }
const cel: React.CSSProperties = { width: '100%', fontSize: '0.8rem', padding: '3px 5px' }

export default function LayoutPostos() {
  const [lista, setLista] = useState<ItemLista[]>([])
  const [carregando, setCarregando] = useState(false)
  const [selecionado, setSelecionado] = useState<string | null>(null) // '' = novo
  const [form, setForm] = useState<Layout | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [msg, setMsg] = useState<{ texto: string; ok: boolean } | null>(null)

  async function carregarLista() {
    setCarregando(true)
    try { setLista((await api.get('/layout-posto')).data) }
    finally { setCarregando(false) }
  }
  useEffect(() => { carregarLista() }, [])

  async function selecionar(cod: string) {
    setMsg(null)
    const d = (await api.get(`/layout-posto/${cod}`)).data
    setSelecionado(cod)
    setForm({
      layposcod: String(d.layposcod), layposdes: d.layposdes, laypossta: d.laypossta,
      linhas: d.linhas.length
        ? d.linhas.map((l: any) => ({ ...l, layposini: String(l.layposini), laypostam: String(l.laypostam), laydcmcam: String(l.laydcmcam) }))
        : [linhaVazia(1)],
    })
  }
  function novo() { setSelecionado(''); setForm({ ...NOVO, linhas: [linhaVazia(1)] }); setMsg(null) }

  const setCab = (k: 'layposcod' | 'layposdes' | 'laypossta', v: string) => setForm(f => f && { ...f, [k]: v })
  const setLinha = (i: number, k: keyof Linha, v: string) =>
    setForm(f => f && { ...f, linhas: f.linhas.map((l, j) => (j === i ? { ...l, [k]: v } : l)) })
  const addLinha = () => setForm(f => f && { ...f, linhas: [...f.linhas, linhaVazia(Math.max(0, ...f.linhas.map(l => Number(l.layposini) || 0)) + 1)] })
  const delLinha = (i: number) => setForm(f => f && { ...f, linhas: f.linhas.filter((_, j) => j !== i) })

  async function salvar() {
    if (!form) return
    setSalvando(true); setMsg(null)
    try {
      if (selecionado) await api.put(`/layout-posto/${selecionado}`, form)
      else await api.post('/layout-posto', form)
      const cod = selecionado || String(Number(form.layposcod))
      await carregarLista(); await selecionar(cod)
      setMsg({ texto: selecionado ? 'Alterações salvas.' : 'Layout cadastrado.', ok: true })
    } catch (e: any) { setMsg({ texto: e?.response?.data?.error || 'Erro ao salvar.', ok: false }) }
    finally { setSalvando(false) }
  }

  async function excluir() {
    if (!selecionado || !window.confirm(`Excluir o layout ${selecionado}?`)) return
    setSalvando(true); setMsg(null)
    try {
      await api.delete(`/layout-posto/${selecionado}`)
      setSelecionado(null); setForm(null); await carregarLista()
    } catch (e: any) { setMsg({ texto: e?.response?.data?.error || 'Erro ao excluir.', ok: false }) }
    finally { setSalvando(false) }
  }

  return (
    <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
      <div className="card" style={{ width: 280, flexShrink: 0 }}>
        <h3 style={{ fontSize: '1rem', fontWeight: 700, marginBottom: '0.9rem', color: '#1a6ea8', paddingBottom: '0.6rem', borderBottom: '2px solid #eff6ff' }}>Layout Postos</h3>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.75rem' }}>
          <button className="btn btn-primary btn-sm" onClick={novo}>+ Novo</button>
        </div>
        {carregando && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Carregando...</p>}
        {!carregando && lista.length === 0 && <p style={{ color: '#6b7280', fontSize: '0.85rem' }}>Nenhum layout cadastrado.</p>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 560, overflowY: 'auto' }}>
          {lista.map(l => {
            const ativo = selecionado === String(l.layposcod)
            return (
              <button key={l.layposcod} onClick={() => selecionar(String(l.layposcod))} style={{
                textAlign: 'left', border: '1px solid #e5e7eb', borderLeft: '4px solid #1a6ea8', borderRadius: 6, padding: '8px 10px', cursor: 'pointer',
                background: ativo ? '#eff6ff' : '#f8fafc', borderColor: ativo ? '#1a6ea8' : '#e5e7eb',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontWeight: 700, fontSize: '0.85rem' }}>{l.layposcod}</span>
                  <span style={{ fontSize: '0.8rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{l.layposdes}</span>
                  {l.laypossta === 'I' && <span style={{ fontSize: '0.66rem', fontWeight: 700, color: '#fff', background: '#9ca3af', borderRadius: 4, padding: '0.1rem 0.4rem' }}>Inativo</span>}
                </div>
                <div style={{ fontSize: '0.76rem', color: '#374151', marginTop: 3 }}>{l.linhas} campo(s)</div>
              </button>
            )
          })}
        </div>
      </div>

      <div className="card" style={{ flex: 1, minWidth: 0 }}>
        {selecionado === null && <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Selecione um layout na lista ao lado, ou clique em "+ Novo".</p>}
        {selecionado !== null && form && (
          <>
            <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#1a6ea8', marginBottom: '0.6rem', paddingBottom: '0.5rem', borderBottom: '2px solid #eff6ff' }}>
              {selecionado ? `Layout ${selecionado} — ${form.layposdes}` : 'Novo Layout'}
            </h3>
            <div style={secao}>
              <Campo label="Código" width={70}>
                <input type="text" inputMode="numeric" maxLength={3} value={form.layposcod} disabled={!!selecionado}
                  onChange={e => setCab('layposcod', e.target.value.replace(/\D/g, ''))} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Descrição" width={340}>
                <input type="text" maxLength={40} value={form.layposdes} onChange={e => setCab('layposdes', e.target.value.toUpperCase())} style={{ width: '100%' }} />
              </Campo>
              <Campo label="Status do Layout" width={120}>
                <select value={form.laypossta} onChange={e => setCab('laypossta', e.target.value)} style={{ width: '100%' }}>
                  <option value="A">Ativo</option>
                  <option value="I">Inativo</option>
                </select>
              </Campo>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8, marginBottom: '0.55rem', width: 'fit-content', maxWidth: '100%' }}>
              <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                <thead><tr>
                  <th style={{ ...th, width: 60 }}>Pos. Inicial</th>
                  <th style={{ ...th, width: 75 }}>Tamanho</th>
                  <th style={{ ...th, width: 130 }}>Tipo</th>
                  <th style={{ ...th, width: 90 }}>Decimais</th>
                  <th style={{ ...th, width: 190 }}>Nome do Atributo</th>
                  <th style={{ ...th, width: 100 }}>Tip. Leitura</th>
                  <th style={{ ...th, width: 180 }}>Valor Constante</th>
                  <th style={{ ...th, width: 34 }}></th>
                </tr></thead>
                <tbody>
                  {form.linhas.map((l, i) => {
                    const atrConhecido = !l.layatrnom || ATRIBUTOS.some(([c]) => c === l.layatrnom)
                    return (
                      <tr key={i}>
                        <td style={td}><input type="text" inputMode="numeric" maxLength={2} value={l.layposini} onChange={e => setLinha(i, 'layposini', e.target.value.replace(/\D/g, ''))} style={{ ...cel, textAlign: 'right' }} /></td>
                        <td style={td}><input type="text" inputMode="numeric" maxLength={5} value={l.laypostam} onChange={e => setLinha(i, 'laypostam', e.target.value.replace(/\D/g, ''))} style={{ ...cel, textAlign: 'right' }} /></td>
                        <td style={td}>
                          <select value={l.laytipcam} onChange={e => setLinha(i, 'laytipcam', e.target.value)} style={cel}>
                            {TIPOS.map(([c, d]) => <option key={c} value={c}>{d}</option>)}
                          </select>
                        </td>
                        <td style={td}>
                          <select value={l.laydcmcam} onChange={e => setLinha(i, 'laydcmcam', e.target.value)} style={cel}>
                            {DECIMAIS.map(([c, d]) => <option key={c} value={c}>{d}</option>)}
                          </select>
                        </td>
                        <td style={td}>
                          <select value={l.layatrnom} onChange={e => setLinha(i, 'layatrnom', e.target.value)} style={cel}>
                            <option value="">—</option>
                            {!atrConhecido && <option value={l.layatrnom}>{l.layatrnom}</option>}
                            {ATRIBUTOS.map(([c, d]) => <option key={c} value={c}>{d}</option>)}
                          </select>
                        </td>
                        <td style={td}>
                          <select value={l.laytiplei} onChange={e => setLinha(i, 'laytiplei', e.target.value)} style={cel}>
                            {LEITURAS.map(([c, d]) => <option key={c} value={c}>{d}</option>)}
                          </select>
                        </td>
                        <td style={td}><input type="text" maxLength={20} value={l.layvalcons} onChange={e => setLinha(i, 'layvalcons', e.target.value)} style={cel} /></td>
                        <td style={{ ...td, textAlign: 'center' }}>
                          <button title="Remover linha" onClick={() => delLinha(i)} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: '1rem' }}>×</button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            <div style={{ marginBottom: '0.8rem' }}>
              <button className="btn btn-sm" onClick={addLinha}>+ Adicionar linha</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <button className="btn btn-primary" onClick={salvar} disabled={salvando}>{salvando ? 'Salvando...' : selecionado ? 'Salvar alterações' : 'Cadastrar'}</button>
              {selecionado && <button className="btn" onClick={excluir} disabled={salvando} style={{ color: '#b91c1c' }}>Excluir</button>}
              {msg && (
                <span style={{ fontSize: '0.8rem', fontWeight: 600, padding: '0.35rem 0.8rem', borderRadius: 6, background: msg.ok ? '#d1fae5' : '#fee2e2', color: msg.ok ? '#065f46' : '#991b1b' }}>{msg.texto}</span>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}
